import React, { useEffect, useState, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateGolfSpotReservationExtendTime } from '../../../api/golf';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../../utils/methods';

const GolfExtendTimeModal = ({ open, handleClose, spotData }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      extensionTime: '3',
      customTime: '',
      startTime: '19:50',
      endTime: '20:46',
    },
  });

  const [extensionTime, setExtensionTime] = useState('3');
  const [customTime, setCustomTime] = useState('');
  const [isHighlighted, setIsHighlighted] = useState(false);
  const previousEndTimeRef = useRef('');

  // ISO 8601 형식의 시간을 시분 형식으로 변환
  const formatTimeFromISO = isoString => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return isoString;
    }
  };

  // 연장 시간을 추가한 새로운 종료 시간 계산
  const calculateNewEndTime = () => {
    if (!spotData?.reservation?.endTime) return '';
    try {
      const endDate = new Date(spotData.reservation.endTime);
      const extensionMinutes = extensionTime === 'custom' ? parseInt(customTime) || 0 : parseInt(extensionTime) || 0;
      const newEndDate = new Date(endDate.getTime() + extensionMinutes * 60000);
      const hours = String(newEndDate.getHours()).padStart(2, '0');
      const minutes = String(newEndDate.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return formatTimeFromISO(spotData?.reservation?.endTime);
    }
  };

  // 종료 시간 변경 감지 및 하이라이트 효과
  useEffect(() => {
    if (!spotData?.reservation?.endTime) return;

    try {
      const endDate = new Date(spotData.reservation.endTime);
      const extensionMinutes = extensionTime === 'custom' ? parseInt(customTime) || 0 : parseInt(extensionTime) || 0;
      const newEndDate = new Date(endDate.getTime() + extensionMinutes * 60000);
      const hours = String(newEndDate.getHours()).padStart(2, '0');
      const minutes = String(newEndDate.getMinutes()).padStart(2, '0');
      const currentEndTime = `${hours}:${minutes}`;

      if (previousEndTimeRef.current && previousEndTimeRef.current !== currentEndTime) {
        setIsHighlighted(true);
        // 1.5초 후 하이라이트 제거
        const timer = setTimeout(() => {
          setIsHighlighted(false);
        }, 1500);
        previousEndTimeRef.current = currentEndTime;
        return () => clearTimeout(timer);
      }
      previousEndTimeRef.current = currentEndTime;
    } catch (error) {
      // 에러 발생 시 처리
    }
  }, [extensionTime, customTime, spotData?.reservation?.endTime]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '600px',
    maxWidth: '95vw',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const handleExtensionTimeChange = event => {
    setExtensionTime(event.target.value);
    setValue('extensionTime', event.target.value);
  };

  const handleCustomTimeChange = event => {
    setCustomTime(event.target.value);
    setValue('customTime', event.target.value);
  };

  const mutation = useMutation({
    mutationFn: data => updateGolfSpotReservationExtendTime(user.token, data),
    onSuccess: () => {
      toast.success('시간 연장이 완료되었습니다.');
      // 타석 데이터 최신화
      const areaId = spotData?.areaId || spotData?.golfAreaId;
      if (areaId) {
        queryClient.invalidateQueries({ queryKey: ['golfSpots', user?.token, areaId] });
      }
      handleClose();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  const onSubmit = () => {
    const extensionTimeValue = watch('extensionTime');
    const customTimeValue = watch('customTime');

    // 연장할 시간(분) 계산
    const extensionMinutes =
      extensionTimeValue === 'custom' ? parseInt(customTimeValue) || 0 : parseInt(extensionTimeValue) || 0;

    // 현재 종료 시간에 연장 시간을 더한 새로운 종료 시간 계산
    if (!spotData?.reservation?.endTime) {
      toast.error('종료 시간 정보를 찾을 수 없습니다.');
      return;
    }

    // 원래 endTime에 연장 시간(분)만 더하기
    // 서버는 UTC 형식을 요구하지만, 시간대 변환 없이 그대로 UTC로 간주
    // 예: 한국 시간 18:40 -> UTC 18:40 (9시간 차이 없음)
    const endTimeString = spotData.reservation.endTime;
    let extensionTime;

    // 시간대 정보가 없으면 한국 시간을 그대로 UTC로 간주하여 ISO 형식으로 변환
    if (!endTimeString.includes('Z') && !endTimeString.includes('+') && !endTimeString.includes('-', 10)) {
      // 시간대 정보가 없는 경우, 문자열을 직접 파싱하여 분만 더하기
      // 예: "2025-12-24T18:25:57" -> "2025-12-24T18:40:57.000Z"
      const timeMatch = endTimeString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);
      if (timeMatch) {
        const [, yearStr, monthStr, dayStr, hoursStr, minutesStr, secondsStr, millisecondsStr] = timeMatch;
        let year = parseInt(yearStr, 10);
        let month = parseInt(monthStr, 10);
        let day = parseInt(dayStr, 10);
        let hours = parseInt(hoursStr, 10);
        let minutes = parseInt(minutesStr, 10);
        const seconds = parseInt(secondsStr, 10);
        const milliseconds = millisecondsStr ? parseInt(millisecondsStr.padEnd(3, '0').substring(0, 3), 10) : 0;

        // 분에 연장 시간 더하기
        minutes += extensionMinutes;

        // 분이 60을 넘으면 시간 증가
        hours += Math.floor(minutes / 60);
        minutes = minutes % 60;

        // 시간이 24를 넘으면 날짜 증가
        day += Math.floor(hours / 24);
        hours = hours % 24;

        // 날짜가 월의 일수를 넘으면 월 증가
        const daysInMonth = new Date(year, month, 0).getDate();
        if (day > daysInMonth) {
          day -= daysInMonth;
          month += 1;
          if (month > 12) {
            month = 1;
            year += 1;
          }
        }

        // UTC로 간주하여 ISO 8601 형식으로 변환 (시간대 변환 없이)
        // Date.UTC()를 사용하여 UTC 시간으로 직접 생성
        const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds));
        // ISO 형식을 "YYYY-MM-DD HH:mm:ss" 형식으로 변환
        const isoString = utcDate.toISOString();
        extensionTime = isoString
          .replace('T', ' ')
          .replace('Z', '')
          .replace(/\.\d{3}$/, '');
      } else {
        // 파싱 실패 시 기존 방식 사용
        const date = new Date(endTimeString + 'Z'); // UTC로 간주
        const newDate = new Date(date.getTime() + extensionMinutes * 60000);
        // ISO 형식을 "YYYY-MM-DD HH:mm:ss" 형식으로 변환
        const isoString = newDate.toISOString();
        extensionTime = isoString
          .replace('T', ' ')
          .replace('Z', '')
          .replace(/\.\d{3}$/, '');
      }
    } else {
      // 시간대 정보가 있으면 그대로 사용
      const currentEndTime = new Date(endTimeString);
      const newEndTime = new Date(currentEndTime.getTime() + extensionMinutes * 60000);
      // ISO 형식을 "YYYY-MM-DD HH:mm:ss" 형식으로 변환
      const isoString = newEndTime.toISOString();
      extensionTime = isoString
        .replace('T', ' ')
        .replace('Z', '')
        .replace(/\.\d{3}$/, '');
    }

    const data = {
      reservationId: spotData.reservation.reservationId,
      endTime: extensionTime,
    };
    mutation.mutate(data);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* 헤더 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '56px',
            px: '32px',
            backgroundColor: '#262626',
            borderRadius: '8px 8px 0 0',
          }}>
          <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>{t('Extend time')}</Typography>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: '32px',
              width: '32px',
              height: '32px',
              p: 0,
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}>
            <CloseIcon />
          </Button>
        </Box>

        {/* 본문 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', p: '32px' }}>
          {/* 연장시간 선택 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Extension time')}</Typography>
              <FormControl>
                <RadioGroup
                  row
                  value={extensionTime}
                  onChange={handleExtensionTimeChange}
                  sx={{ gap: '16px', flexWrap: 'wrap' }}>
                  {['3', '5', '10', '15', '30'].map(time => (
                    <FormControlLabel
                      key={time}
                      value={time}
                      control={
                        <Radio
                          sx={{
                            color: '#D9D9D9',
                            '&.Mui-checked': {
                              color: '#0F7F85',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 24,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000', ml: '8px' }}>
                          {time}분
                        </Typography>
                      }
                      sx={{ margin: 0 }}
                    />
                  ))}
                  <FormControlLabel
                    value="custom"
                    control={
                      <Radio
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 24,
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: '8px' }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                          {t('Custom setting')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TextField
                            value={customTime}
                            onChange={handleCustomTimeChange}
                            placeholder={t('Please enter')}
                            disabled={extensionTime !== 'custom'}
                            sx={{
                              width: '120px',
                              height: '48px',
                              '& .MuiInputBase-root': {
                                height: '48px',
                                backgroundColor: extensionTime === 'custom' ? '#ffffff' : '#f2f2f2',
                                border: '1px solid #D9D9D9',
                                borderRadius: '8px',
                                '& fieldset': {
                                  border: 'none',
                                },
                                '& input': {
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: extensionTime === 'custom' ? '#000' : '#A6A6A6',
                                  py: '12px',
                                  px: '16px',
                                },
                                '& input::placeholder': {
                                  color: '#A6A6A6',
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#A6A6A6' }}>
                            {t('minutes')}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ margin: 0 }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* 시작 시간 & 변경된 종료 시간 */}
            <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Start time')}</Typography>
                <TextField
                  value={formatTimeFromISO(spotData?.reservation?.startTime)}
                  disabled
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '48px',
                      backgroundColor: '#F2F2F2',
                      border: '1px solid #D9D9D9',
                      borderRadius: '8px',
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input': {
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#A6A6A6',
                        textAlign: 'center',
                        py: '12px',
                        px: '16px',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                  {t('Changed end time')}
                </Typography>
                <TextField
                  value={calculateNewEndTime()}
                  disabled
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: '48px',
                      backgroundColor: isHighlighted ? '#ffffff' : '#F2F2F2',
                      border: isHighlighted ? '2px solid #0F7F85' : '1px solid #D9D9D9',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease-in-out',
                      animation: isHighlighted ? 'pulse 0.5s ease-in-out' : 'none',
                      '@keyframes pulse': {
                        '0%': {
                          transform: 'scale(1)',
                        },
                        '50%': {
                          transform: 'scale(1.02)',
                        },
                        '100%': {
                          transform: 'scale(1)',
                        },
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& input': {
                        fontSize: '16px',
                        fontWeight: isHighlighted ? '600' : '500',
                        textAlign: 'center',
                        py: '12px',
                        px: '16px',
                        transition: 'all 0.3s ease-in-out',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 버튼 */}
          <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              sx={{
                width: '120px',
                height: '48px',
                backgroundColor: '#D9D9D9',
                color: '#000',
                fontSize: '20px',
                fontWeight: '600',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#D9D9D9' },
              }}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              sx={{
                width: '240px',
                height: '48px',
                backgroundColor: '#0F7F85',
                color: '#fff',
                fontSize: '20px',
                fontWeight: '600',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#0F7F85' },
              }}>
              {t('Extension complete')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfExtendTimeModal;
